import { DeviceEnergyflow, HeatpumpEnergyflowEntry } from '@core/models/device-energyflow.model';
import {
  InstallationTelemetry,
  Kind,
  MeasurementPoint,
  Point,
  Quality,
  Slot,
  Total,
} from '@core/models/installation.model';
import { heatpumpSources } from './device-energyflow';
import { sourceGroup, sourceLabel } from './installation-card-set';
import { intervalsBySlot, seriesTotal } from './pv-forecast';

type HeatpumpField = keyof Omit<HeatpumpEnergyflowEntry, 'quality' | 'channelNumber'>;

interface HeatpumpSeriesSpec {
  role: string;
  field: HeatpumpField;
  kind: Kind;
  unit: string;
  nameKey: string;
}

// In card order. Their layout lives in ENERGYFLOW_LAYOUT under these codes.
export const HEATPUMP_SERIES: readonly HeatpumpSeriesSpec[] = [
  {
    role: 'heatpump_thermal_energy',
    field: 'thermalEnergyInWh',
    kind: 'interval',
    unit: 'Wh',
    nameKey: 'installations.insights.heatpump.thermalEnergy',
  },
  {
    role: 'heatpump_coefficient_of_performance',
    field: 'coefficientOfPerformance',
    kind: 'average',
    unit: 'ratio',
    nameKey: 'installations.insights.heatpump.coefficientOfPerformance',
  },
];

const QUALITIES: ReadonlySet<string> = new Set<Quality>(['good', 'partial', 'missing', 'reset']);

// Shaped like the points the installation read API serves, so the cards, the source strip and the
// data explorer take them without a branch of their own.
export interface HeatpumpSeries {
  points: MeasurementPoint[];
  telemetry: Point[];
}

function slotFor(time: string, kind: Kind, value: number | null, quality: string | null): Slot {
  if (value === null) {
    return { time, value: null, quantity: null, quality: 'missing', sourceIndex: null };
  }
  const known = quality !== null && QUALITIES.has(quality) ? (quality as Quality) : 'partial';
  return {
    time,
    value,
    quantity: kind === 'interval' ? value : null,
    quality: known,
    sourceIndex: 0,
  };
}

// Summed for an interval and averaged otherwise, as the read API totals a point.
function totalFor(kind: Kind, values: (number | null)[]): Total {
  const sum = seriesTotal(values);
  const count = values.filter(value => value !== null).length;
  if (kind === 'interval') {
    return { value: null, quantity: sum, quality: 'partial' };
  }
  return { value: sum === null ? null : sum / count, quantity: null, quality: 'partial' };
}

// A series is only made when the period has a value for it, so a heat pump that never reports a
// field does not get an empty line for it on every view.
export function heatpumpSeries(
  points: MeasurementPoint[],
  energyflowFor: (deviceId: string) => DeviceEnergyflow | null,
  telemetry: InstallationTelemetry | null,
  nameFor: (nameKey: string) => string,
): HeatpumpSeries {
  const result: HeatpumpSeries = { points: [], telemetry: [] };
  if (!telemetry) {
    return result;
  }
  const times = telemetry.points[0]?.slots.map(slot => slot.time) ?? [];
  const sources = heatpumpSources(points);

  for (const anchor of sources) {
    const binding = anchor.binding;
    if (!binding) continue;
    const channel = binding.source.contextNumber ?? 0;
    const entries = intervalsBySlot(
      energyflowFor(String(binding.source.deviceId))?.intervals,
      telemetry,
    ).map(interval => interval?.heatPump?.find(entry => entry.channelNumber === channel) ?? null);

    for (const spec of HEATPUMP_SERIES) {
      const values = entries.map(entry =>
        entry && entry.quality !== 'missing' ? (entry[spec.field] ?? null) : null,
      );
      if (values.every(value => value === null)) continue;

      const pointOid = `energyflow:${sourceGroup(anchor)}:${spec.role}`;
      const name =
        sources.length > 1
          ? `${nameFor(spec.nameKey)} (${sourceLabel(anchor) ?? ''})`
          : nameFor(spec.nameKey);
      const source = { ...binding.source, valueName: null };

      result.points.push({
        pointOid,
        name,
        role: spec.role,
        roleDisplayName: name,
        nature: 'measured',
        kind: spec.kind,
        unit: spec.unit,
        state: 'active',
        binding: { ...binding, bindingOid: pointOid, source, sign: 1, scale: 1 },
        bindingState: 'bound',
        required: false,
      });
      result.telemetry.push({
        pointId: pointOid,
        name,
        role: spec.role,
        roleDisplayName: name,
        kind: spec.kind,
        nature: 'measured',
        unit: spec.unit,
        state: 'active',
        sources: [
          {
            from: binding.validFrom,
            to: binding.validTo,
            source,
            provenance: binding.provenance,
            sign: 1,
            scale: 1,
            offCatalogue: false,
          },
        ],
        total: totalFor(spec.kind, values),
        slots: times.map((time, index) =>
          slotFor(time, spec.kind, values[index] ?? null, entries[index]?.quality ?? null),
        ),
      });
    }
  }
  return result;
}
